import "twin.macro";

import { useFormContext } from "react-hook-form";
import { useWatch } from "react-hook-form";

import { Close, Edit } from "@mui/icons-material";
import { Button, IconButton, Typography } from "@mui/material";

import { FaIcon } from "@features/ui";

import {
  CldImage,
  getResourceUrl,
  useOpenCloudinaryWidget,
} from "../../../services/cloudinary";
import { ControlledTextInput } from "../../../utils/forms/ControlledInputs";
import BlockCard from "../../ui/BlockCard";

const dirty = {
  shouldDirty: true,
};

const Image = ({ handleUpload }) => {
  const { setValue } = useFormContext();
  const cloudinaryId = useWatch({ name: "cloudinaryId" });

  return (
    <div tw="w-44">
      {cloudinaryId && (
        <div tw="relative w-full aspect-square" className="group">
          <CldImage
            tw="w-full h-full object-cover object-center rounded-lg border-2 border-neutral-900/10"
            size="medium"
            publicId={cloudinaryId}
          />
          <div tw="absolute top-1 right-1 opacity-0 px-2 bg-neutral-900/50 text-white group-hover:opacity-100 flex items-center rounded-full">
            <IconButton
              title="Change Image"
              size="small"
              tw="text-white"
              onClick={handleUpload}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              title="Remove Image"
              size="small"
              tw="text-white"
              onClick={() =>
                setValue("cloudinaryId", null, { shouldDirty: true })
              }
            >
              <Close />
            </IconButton>
          </div>
        </div>
      )}
      {!cloudinaryId && (
        <button
          tw="w-full aspect-square rounded-lg border-2 border-neutral-900/10 bg-neutral-100 flex flex-col items-center justify-center hover:bg-neutral-200 transition-colors"
          onClick={handleUpload}
        >
          <FaIcon icon="image" tw="text-neutral-300 text-2xl" />
          <p tw="text-neutral-600 mt-4">Add Thumbnail</p>
        </button>
      )}
      <p tw="text-neutral-500 text-sm mt-2">Ideal size: 400 × 400px</p>
    </div>
  );
};

const AdditionalFile = ({ handleUpload }) => {
  const { setValue } = useFormContext();
  const additionalFileCloudinaryId = useWatch({
    name: "additionalFileCloudinaryId",
  });
  const additionalFileResourceType = useWatch({
    name: "additionalFileResourceType",
  });
  const additionalFileName = useWatch({ name: "additionalFileName" });

  const handleResetAdditionalFile = () => {
    setValue("additionalFileCloudinaryId", null, dirty);
    setValue("additionalFileName", null, dirty);
    setValue("additionalFileResourceType", null, dirty);
  };

  return (
    <div>
      {additionalFileName ? (
        <div tw="flex text-sm items-center max-w-full gap-3 rounded bg-neutral-100 px-2 py-1">
          <h3 tw="text-neutral-600">Additional File:</h3>
          <Typography tw="text-sm" noWrap>
            <a
              tw="hover:underline"
              href={getResourceUrl(additionalFileCloudinaryId, {
                resourceType: additionalFileResourceType,
              })}
              download={additionalFileName}
              target="_blank"
              rel="noreferrer"
            >
              {additionalFileName}
            </a>
          </Typography>
          <IconButton
            title="Remove File"
            tw="shrink-0 ml-auto text-neutral-400"
            size="small"
            onClick={handleResetAdditionalFile}
          >
            <Close tw="text-lg" />
          </IconButton>
        </div>
      ) : (
        <Button
          variant="outlined"
          // size="small"
          startIcon={<FaIcon icon="arrow-up-from-line" />}
          onClick={() => handleUpload("file")}
        >
          Upload Additional File
        </Button>
      )}
      <p tw="text-neutral-500 text-sm mt-2">
        Users can download additional information.
      </p>
    </div>
  );
};

const General = () => {
  const { setValue, control } = useFormContext();
  const openCloudinaryWidget = useOpenCloudinaryWidget();

  const handleUpload = (type: "image" | "file") =>
    openCloudinaryWidget({
      config:
        type === "image" ? { clientAllowedFormats: ["image"] } : undefined,
      onSuccess: (file) => {
        if (type === "image") {
          setValue("cloudinaryId", file.info.public_id, { shouldDirty: true });
        } else {
          setValue("additionalFileCloudinaryId", file.info.public_id, dirty);
          setValue(
            "additionalFileName",
            file.info.original_filename +
              "." +
              file.info.path.split(".").at(-1),
            dirty
          );
          setValue(
            "additionalFileResourceType",
            file.info.resource_type,
            dirty
          );
        }
      },
    });

  return (
    <BlockCard title="Branding">
      <div tw="grid grid-cols-[auto_1fr] gap-6">
        <Image handleUpload={() => handleUpload("image")} />
        <div tw="space-y-4">
          <ControlledTextInput control={control} label="Name" name="name" />
          <ControlledTextInput
            control={control}
            multiline
            minRows={4}
            maxRows={8}
            label="Description"
            name="description"
          />
          <AdditionalFile handleUpload={() => handleUpload("file")} />
        </div>
      </div>
    </BlockCard>
  );
};

export default General;
